/**
 * RouteErrorBoundary.jsx — Fix #5c (2026-07-07).
 *
 * Class-component error boundary wrapped around the Routes tree so a
 * silent render crash surfaces as a proper error page instead of a
 * blank white viewport.
 *
 * Behaviour:
 *   • Catches errors thrown during render, in lifecycle methods, and in
 *     constructors of the whole child tree.
 *   • Renders a recovery panel with a Refresh button that calls
 *     `window.location.reload()` and a mailto Contact-support link.
 *   • Resets its own error state when the pathname changes, so a broken
 *     page doesn't lock the user out of the whole app forever — they
 *     can click Nav → another route and it recovers on the next mount.
 *   • Best-effort telemetry ping so we can see how often this triggers
 *     in production. Non-blocking; failure to POST is ignored.
 *
 * Only guaranteed to catch RENDER-phase crashes. Async errors (rejected
 * promises inside effects, event handlers) still need their own local
 * try/catch → toast paths. That's fine — this boundary exists to stop
 * the blank white viewport regression, not to be an omnipotent shield.
 */
import { Component } from "react";
import { AlertTriangle, RefreshCw, LifeBuoy } from "lucide-react";

class RouteErrorBoundaryInner extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, path: props.pathname };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        // Reset when the route changes — a fresh page render deserves a
        // fresh chance to succeed even if the previous page crashed.
        if (nextProps.pathname !== prevState.path) {
            return { hasError: false, error: null, path: nextProps.pathname };
        }
        return null;
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error("[RouteErrorBoundary] caught:", error, info);
        try {
            const backend = process.env.REACT_APP_BACKEND_URL;
            if (backend && typeof navigator !== "undefined" && navigator.doNotTrack !== "1") {
                fetch(`${backend}/api/telemetry/event`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    keepalive: true,
                    body: JSON.stringify({
                        event: "route_error_boundary_triggered",
                        session_id: (typeof sessionStorage !== "undefined"
                            && sessionStorage.getItem("coda.telemetry.session")) || null,
                        meta: {
                            pathname: this.props.pathname,
                            message: String(error?.message || error).slice(0, 300),
                            component_stack: String(info?.componentStack || "").slice(0, 800),
                        },
                    }),
                }).catch(() => {});
            }
        } catch (_) { /* swallow — telemetry never blocks recovery */ }
    }

    render() {
        if (!this.state.hasError) return this.props.children;
        const errMsg = String(this.state.error?.message || this.state.error || "").slice(0, 300);
        return (
            <div
                role="alert"
                data-testid="route-error-boundary"
                style={{
                    minHeight: "60vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "var(--paper, #F8FAFC)",
                    padding: "48px 24px",
                }}
            >
                <div
                    style={{
                        maxWidth: 520,
                        background: "#FFFFFF",
                        border: "0.5px solid #E2E8F0",
                        borderRadius: 14,
                        padding: "28px 30px",
                        boxShadow: "0 6px 22px rgba(15, 23, 42, 0.06)",
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                        <AlertTriangle size={20} color="#D97706" />
                        <div style={{
                            fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                            letterSpacing: "0.13em", color: "#B45309",
                        }}>
                            Something snapped
                        </div>
                    </div>
                    <h2 style={{
                        fontSize: 22, fontWeight: 600, color: "#0F172A",
                        margin: "6px 0 6px 0", lineHeight: 1.25,
                    }}>
                        This page hit a snag
                    </h2>
                    <p style={{
                        fontSize: 13.5, color: "#475569", lineHeight: 1.55, margin: 0,
                    }}>
                        Refresh to retry, or ping support if the problem persists.
                    </p>
                    {errMsg && (
                        <pre style={{
                            marginTop: 14, padding: "10px 12px",
                            background: "#F8FAFC", borderRadius: 8,
                            fontSize: 11.5, lineHeight: 1.5,
                            color: "#334155", overflow: "auto", maxHeight: 160,
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                            whiteSpace: "pre-wrap",
                        }}>{errMsg}</pre>
                    )}
                    <div style={{ marginTop: 18, display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <button
                            type="button"
                            onClick={() => window.location.reload()}
                            data-testid="route-error-refresh"
                            style={{
                                display: "inline-flex", alignItems: "center", gap: 6,
                                padding: "8px 14px", background: "#1A237E", color: "#FFFFFF",
                                border: 0, borderRadius: 8, fontSize: 12, fontWeight: 600,
                                cursor: "pointer",
                            }}
                        >
                            <RefreshCw size={13} /> Refresh
                        </button>
                        <a
                            href="mailto:research@coda.systems?subject=Something%20snapped%20on%20CODA"
                            data-testid="route-error-support"
                            style={{
                                display: "inline-flex", alignItems: "center", gap: 6,
                                padding: "8px 14px", background: "#FFFFFF", color: "#0F172A",
                                border: "0.5px solid #CBD5E1", borderRadius: 8,
                                fontSize: 12, fontWeight: 600, textDecoration: "none",
                            }}
                        >
                            <LifeBuoy size={13} /> Report this
                        </a>
                    </div>
                </div>
            </div>
        );
    }
}

// Functional wrapper so we can read `useLocation()` and pass the path
// down as a prop — enables the "reset on route change" behaviour.
import { useLocation } from "react-router-dom";
export default function RouteErrorBoundary({ children }) {
    const location = useLocation();
    return (
        <RouteErrorBoundaryInner pathname={location.pathname}>
            {children}
        </RouteErrorBoundaryInner>
    );
}
